import {useEffect, useRef, useState} from 'react';
import moment from 'moment';
import DirectApi from '../../../directApi';

/*
 * When All invoices offers Import from Xero, and which Xero organisations the
 * import modal can use. Both read GET xero/organizations, which answers 200 {success: false} for
 * any failure behind it (XeroIntegrationLogic.getXeroOrganizations), so the body decides, never
 * the status alone.
 */

export type XeroImportGate = {
	flagOn: boolean;
	canCreateInvoices: boolean;
	newXeroEnabled: boolean;
	pathwaysOn: boolean;
	// Today's Synchronise rule: an integration is connected, and a Sage PSA restricted account
	// has financial access.
	showSyncAndExport: boolean;
};

/** Every condition except the organisation count, which needs a request. */
export const canOfferXeroInvoiceImport = (gate: XeroImportGate): boolean =>
	gate.flagOn && gate.canCreateInvoices && gate.newXeroEnabled && !gate.pathwaysOn && gate.showSyncAndExport;

export type XeroOrganisation = {
	tenantId: string;
	name: string;
	// The connection's token has expired or been revoked: the user has to reconnect it.
	inaccessible: boolean;
};

/** The organisations in a xero/organizations body, or null when the body is not a success. */
export const parseXeroOrganisations = (body: unknown): XeroOrganisation[] | null => {
	if (body === null || typeof body !== 'object') {
		return null;
	}
	const {success, orgs} = body as {success?: unknown; orgs?: unknown};
	if (success !== true || !Array.isArray(orgs)) {
		return null;
	}
	return orgs
		.filter(org => org !== null && typeof org === 'object' && typeof org.tenantId === 'string')
		.map(org => ({tenantId: org.tenantId, name: String(org.organization ?? ''), inaccessible: Boolean(org.inaccessible)}));
};

/** The connected organisations, or null when they cannot be read. Never rejects. */
export const fetchXeroOrganisations = async (): Promise<XeroOrganisation[] | null> => {
	try {
		const result: Response = await DirectApi.Fetch_Raw('xero/organizations');
		if (result.status !== 200) {
			return null;
		}
		return parseXeroOrganisations(await result.json());
	} catch {
		return null;
	}
};

// All invoices is remounted on every date range change (FinanceRoutes keys its query renderer by the
// dates), so the count is shared across mounts for a short while instead of asking Xero each time.
// Only a count of 1 or more is kept: after a failure or an empty answer the next mount asks again.
// The modal re-reads the organisations on every open, so an expired connection is still caught when
// it matters.
const COUNT_TTL_MS = 5 * 60 * 1000;
// value is set once the request has answered, so a remount can start from it without a blink.
type CountEntry = {at: number; count: Promise<number | null>; value?: number};
const countCache = new Map<string, CountEntry>();

/** Clears the shared count (tests). */
export const resetXeroOrganisationCountCache = (): void => countCache.clear();

const freshEntry = (viewerKey: string): CountEntry | undefined => {
	const cached = countCache.get(viewerKey);
	return cached && Date.now() - cached.at < COUNT_TTL_MS ? cached : undefined;
};

const connectedCount = (viewerKey: string): Promise<number | null> => {
	const cached = freshEntry(viewerKey);
	if (cached) {
		return cached.count;
	}
	const at = Date.now();
	const count = fetchXeroOrganisations().then(orgs => {
		// Only touch the entry this request created: a newer request may have replaced it meanwhile.
		if (countCache.get(viewerKey)?.count !== count) {
			return orgs ? orgs.length : null;
		}
		// Keep only a count of 1 or more. A failed read is not kept, and neither is 0: the xero service
		// drops an organisation it could not reach (a rate limit, a 5xx) and still answers success.
		if (!orgs || orgs.length === 0) {
			countCache.delete(viewerKey);
			return orgs ? 0 : null;
		}
		countCache.set(viewerKey, {at, count, value: orgs.length});
		return orgs.length;
	});
	countCache.set(viewerKey, {at, count});
	return count;
};

/**
 * How many Xero organisations are connected, inaccessible ones included: null until known, 0 when
 * they cannot be read (the button stays hidden). Asks only once enabled, at most once per mount, and reuses a
 * recent answer for the same company across remounts, so a flag-off page, a re-render or a date range
 * change makes no new request.
 */
export const useConnectedXeroOrganisationCount = (enabled: boolean, viewerKey: string): number | null => {
	const [count, setCount] = useState<number | null>(() => (enabled ? freshEntry(viewerKey)?.value ?? null : null));
	const asked = useRef(false);
	const mounted = useRef(true);

	useEffect(
		() => () => {
			mounted.current = false;
		},
		[]
	);

	useEffect(() => {
		if (!enabled || asked.current) {
			return;
		}
		asked.current = true;
		connectedCount(viewerKey).then(result => {
			if (mounted.current) {
				setCount(result === null ? 0 : result);
			}
		});
	}, [enabled, viewerKey]);

	return count;
};

/*
 * The Xero invoices the import modal lists: sales invoices dated in a range that no Forecast invoice
 * links to. GET xero/unlinkedInvoices answers one status per organisation, so one organisation that
 * fails never hides another's rows.
 */

/** The longest Invoice date range, in calendar months. The xero service holds the same rule. */
export const MAX_INVOICE_DATE_RANGE_MONTHS = 2;

/**
 * Whether the list can be asked for this range: both real days, the end not before the start, and
 * the end at most two calendar months after the start. A day the later month lacks counts as its
 * last day (31 Dec + 2 months = end of February).
 */
export const isInvoiceDateRangeAllowed = (startDate: string, endDate: string): boolean => {
	const start = moment(startDate, 'YYYY-MM-DD', true);
	const end = moment(endDate, 'YYYY-MM-DD', true);
	if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
		return false;
	}
	return !end.isAfter(start.clone().add(MAX_INVOICE_DATE_RANGE_MONTHS, 'months'), 'day');
};

export type UnlinkedXeroOrganisationStatus = 'ok' | 'inaccessible' | 'error' | 'tooMany';

export type UnlinkedXeroOrganisation = {
	tenantId: string;
	status: UnlinkedXeroOrganisationStatus;
	// Rows of this organisation in the list.
	count: number;
};

export type UnlinkedXeroInvoice = {
	invoiceId: string;
	tenantId: string;
	invoiceNumber: string;
	contactId: string;
	contactName: string;
	// The Xero total, in the invoice's own currency.
	total: number;
	currencyCode: string;
	// The Xero invoice date, YYYY-MM-DD.
	date: string;
	status: string;
};

export type UnlinkedXeroInvoices = {
	organisations: UnlinkedXeroOrganisation[];
	invoices: UnlinkedXeroInvoice[];
};

const ORGANISATION_STATUSES: UnlinkedXeroOrganisationStatus[] = ['ok', 'inaccessible', 'error', 'tooMany'];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const parseOrganisation = (org: unknown): UnlinkedXeroOrganisation | null => {
	if (!isObject(org) || typeof org.tenantId !== 'string') {
		return null;
	}
	const status = ORGANISATION_STATUSES.includes(org.status as UnlinkedXeroOrganisationStatus)
		? (org.status as UnlinkedXeroOrganisationStatus)
		: 'error';
	const count = status === 'ok' && typeof org.count === 'number' && Number.isFinite(org.count) ? org.count : 0;
	return {tenantId: org.tenantId, status, count};
};

const parseInvoice = (row: unknown): UnlinkedXeroInvoice | null => {
	if (
		!isObject(row) ||
		typeof row.invoiceId !== 'string' ||
		typeof row.tenantId !== 'string' ||
		typeof row.total !== 'number' ||
		!Number.isFinite(row.total) ||
		typeof row.date !== 'string' ||
		!ISO_DAY.test(row.date)
	) {
		return null;
	}
	return {
		invoiceId: row.invoiceId,
		tenantId: row.tenantId,
		invoiceNumber: text(row.invoiceNumber),
		contactId: text(row.contactId),
		contactName: text(row.contactName),
		total: row.total,
		currencyCode: text(row.currencyCode),
		date: row.date,
		status: text(row.status),
	};
};

/** The list in a xero/unlinkedInvoices body, or null when the body is not one. Malformed rows are left out. */
export const parseUnlinkedXeroInvoices = (body: unknown): UnlinkedXeroInvoices | null => {
	if (!isObject(body) || !Array.isArray(body.organisations) || !Array.isArray(body.invoices)) {
		return null;
	}
	return {
		organisations: body.organisations.map(parseOrganisation).filter((org): org is UnlinkedXeroOrganisation => org !== null),
		invoices: body.invoices.map(parseInvoice).filter((row): row is UnlinkedXeroInvoice => row !== null),
	};
};

/** The unlinked Xero invoices dated in the range, or null when they cannot be read. Never rejects. */
export const fetchUnlinkedXeroInvoices = async (range: {
	startDate: string;
	endDate: string;
}): Promise<UnlinkedXeroInvoices | null> => {
	try {
		const result: Response = await DirectApi.Fetch_Raw(
			`xero/unlinkedInvoices?fromDate=${encodeURIComponent(range.startDate)}&toDate=${encodeURIComponent(range.endDate)}`
		);
		if (result.status !== 200) {
			return null;
		}
		return parseUnlinkedXeroInvoices(await result.json());
	} catch {
		return null;
	}
};
